import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { LocalFeatureTogglerRepository } from './local-feature-toggler.service';
import { RemoteFeatureTogglerService } from './remote-feature-toggler.service';
import { FeatureToggler, FeatureValue } from './feature-toggler';
import { EvaluationContext } from './models/feature-flag.model';

/**
 * A hybrid feature toggler that combines local and remote feature toggling.
 * Local configurations will override remote flags when both are present.
 */
@Injectable()
export class HybridFeatureTogglerService implements FeatureToggler, OnDestroy {
  private _state: Record<string, FeatureValue> = {};
  private featuresState = new Subject<Record<string, FeatureValue>>();
  private remoteLoaded = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();

  /**
   * Observable that emits when the feature flags have been loaded
   */
  readonly loaded$ = this.remoteLoaded.asObservable();

  /**
   * Observable that emits when feature flags state is changed
   * Combines both local and remote feature state changes
   */
  readonly featureStateChanged$: Observable<void>;

  constructor(
    private localToggler: LocalFeatureTogglerRepository,
    private remoteToggler: RemoteFeatureTogglerService
  ) {
    this.featureStateChanged$ = merge(
      this.remoteToggler.featureStateChanged$,
      this.featuresState.pipe(
        tap(state => (this._state = state)),
        map(() => undefined)
      )
    );

    // Initialize the combined state immediately
    this.updateCombinedState();

    // Subscribe to remote toggler loaded state
    this.remoteToggler.loaded$.pipe(takeUntil(this.destroy$)).subscribe(loaded => {
      this.remoteLoaded.next(loaded);
      this.updateCombinedState();
    });
  }

  loadFeatureFlags(): Observable<void> {
    return this.remoteToggler.loadFeatureFlags();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Update the combined state by merging local and remote feature flags
   */
  private updateCombinedState(): void {
    const combinedState: Record<string, FeatureValue> = {
      ...this.remoteToggler.state,
      ...this.localToggler.state,
    };

    this._state = combinedState; // Directly update _state
    this.featuresState.next(combinedState);
  }

  /**
   * Check if a feature is enabled
   * @param featureName The feature flag ID
   * @returns Boolean indicating if the feature is enabled
   */
  isFeatureEnabled(featureName: string): boolean {
    // First check if the feature is in local configuration
    if (featureName in this.localToggler.state) {
      return this.localToggler.isFeatureEnabled(featureName);
    }

    // If not in local config, check remote
    return this.remoteToggler.isFeatureEnabled(featureName);
  }

  /**
   * Identify the user context for feature flag evaluation
   * @param context The user context
   */
  identify(context: EvaluationContext | string): void {
    // Update both local and remote togglers with the same context
    this.remoteToggler.identify(context);
    this.localToggler.identify(context);
  }

  /**
   * Get all feature flags with their evaluation status
   * @returns A record of feature flag IDs to their values
   */
  get state(): Record<string, boolean | FeatureValue> {
    return this._state;
  }
}
